/**
 * Clinic mascot identity (phase 1 — localStorage only).
 *
 * Persona stays data: species / coat / nickname. Voice / animation come later.
 * Nickname is capped at 10 chars for compact "Ask {nickname}" entry points.
 */

export const MASCOT_NICKNAME_MAX_CHARS = 10
export const MASCOT_NAME_MAX_CHARS = 40

export type MascotSpecies = 'regular_dog' | 'cat' | 'horse' | 'rabbit'

/**
 * Coat markings. Each one is a real pattern that occurs in the animal —
 * invented markings look like smudges, and on a vet product they look like
 * mistakes.
 *
 * `stripes` is kept as the id for tabby markings so mascots saved before the
 * artwork was reworked keep their pattern.
 */
export type MascotPattern =
    | 'solid'
    | 'stripes'
    | 'patched'
    | 'speckled'
    | 'bicolor'
    | 'blaze'
    | 'mask'
    | 'eye_patch'

/**
 * Muzzle length and placement.
 *
 * `flat` is wide and sits high on the face (pug, bulldog); `short` is compact
 * and sits mid-face (chihuahua, terrier); `long` reaches down the face
 * (retriever, shepherd). All three share the dog skull — the muzzle is what
 * turns it into a pug or a shepherd, not a second head shape.
 */
export type MascotSnout = 'flat' | 'short' | 'long'

/**
 * Which snouts each species offers. Ordered with the species' native shape
 * first, since that's the one its artwork was drawn with — the others are
 * proportioned from it.
 *
 * Species with a single entry hide the picker entirely.
 */
export const MASCOT_SNOUT_OPTIONS: Record<
    MascotSpecies,
    { id: MascotSnout; label: string }[]
> = {
    regular_dog: [
        { id: 'long', label: 'Long' },
        { id: 'short', label: 'Short' },
        { id: 'flat', label: 'Flat' },
    ],
    cat: [{ id: 'short', label: 'Short' }],
    horse: [{ id: 'long', label: 'Long' }],
    rabbit: [{ id: 'short', label: 'Short' }],
}

export function resolveMascotSnout(
    species: MascotSpecies,
    snout: MascotSnout,
): MascotSnout {
    const options = MASCOT_SNOUT_OPTIONS[species]
    return options.some(o => o.id === snout) ? snout : options[0].id
}

/**
 * Coat length. Drawn as a fringe around the head rather than as a texture —
 * at avatar sizes the silhouette is the only part of a coat that reads.
 */
export type MascotFurType = 'short' | 'curly' | 'long'

export const MASCOT_FUR_OPTIONS: {
    id: MascotFurType
    label: string
}[] = [
    { id: 'short', label: 'Short' },
    { id: 'curly', label: 'Curly' },
    { id: 'long', label: 'Long' },
]

/**
 * Ear shapes are drawn once in a canonical space and re-anchored per species,
 * so a "floppy" ear is the same artwork on a dog or a rabbit — only the
 * attachment point and scale differ. `one_up` is the mismatched look: pointed
 * on one side, floppy on the other.
 */
export type MascotEarStyle =
    | 'pointed'
    | 'floppy'
    | 'folded'
    | 'droopy'
    | 'button'
    | 'upright'
    | 'one_up'

export type ClinicMascotIdentity = {
    species: MascotSpecies
    /** Hex coat color (CSS `--coat-primary`). */
    primaryColor: string
    /** Hex accent / belly / markings (CSS `--coat-secondary`). */
    secondaryColor: string
    /**
     * Hex iris color (CSS `--coat-iris`). The pupil stays near-black
     * regardless — tinting the whole eye reads as a costume lens rather than
     * an eye colour.
     */
    eyeColor: string
    pattern: MascotPattern
    furType: MascotFurType
    snout: MascotSnout
    /**
     * Not every shape suits every species — resolve with
     * {@link resolveMascotEarStyle} before rendering.
     */
    earStyle: MascotEarStyle
    /** Full display name, e.g. "Rocket". */
    name: string
    /**
     * Short label for "Ask {nickname}" (≤10 chars). Required — the entry
     * points read "Ask {nickname}", so an empty nickname leaves a generic
     * label everywhere.
     */
    nickname: string
}

export const MASCOT_SPECIES_OPTIONS: {
    id: MascotSpecies
    label: string
}[] = [
    { id: 'regular_dog', label: 'Dog' },
    { id: 'cat', label: 'Cat' },
    // Horse and rabbit art is parked until those heads are worth offering.
]

/**
 * Species the picker currently offers. Horse / rabbit still exist as
 * artwork, but a saved one has to land on something the clinic can edit.
 */
export function resolveMascotSpecies(species: MascotSpecies): MascotSpecies {
    return MASCOT_SPECIES_OPTIONS.some(o => o.id === species)
        ? species
        : MASCOT_SPECIES_OPTIONS[0].id
}

/**
 * Which markings each species offers, and what they're called there.
 *
 * The same artwork means different things on different animals — the light
 * pattern that makes a beagle is "Irish spotting", the same shape on a cat is
 * a tuxedo, and on a horse it's a bald face. Equally, a tabby's forehead "M"
 * has no equivalent on a horse, and a horse's star has none on a cat. So the
 * list is scoped per species rather than offering every marking on every
 * animal and letting most combinations look wrong.
 *
 * Order matters: the first entry is the species default.
 */
export const MASCOT_PATTERN_OPTIONS: Record<
    MascotSpecies,
    { id: MascotPattern; label: string }[]
> = {
    regular_dog: [
        { id: 'solid', label: 'Solid' },
        { id: 'bicolor', label: 'Irish' },
        { id: 'patched', label: 'Piebald' },
        { id: 'mask', label: 'Mask' },
        { id: 'blaze', label: 'Blaze' },
        { id: 'speckled', label: 'Ticked' },
        { id: 'eye_patch', label: 'Eye patch' },
    ],
    cat: [
        { id: 'solid', label: 'Solid' },
        { id: 'stripes', label: 'Tabby' },
        { id: 'bicolor', label: 'Tuxedo' },
        { id: 'patched', label: 'Calico' },
        { id: 'mask', label: 'Colorpoint' },
        { id: 'eye_patch', label: 'Eye patch' },
    ],
    horse: [
        { id: 'solid', label: 'Solid' },
        { id: 'blaze', label: 'Blaze' },
        { id: 'patched', label: 'Pinto' },
        { id: 'speckled', label: 'Roan' },
        { id: 'bicolor', label: 'Bald face' },
    ],
    rabbit: [
        { id: 'solid', label: 'Solid' },
        { id: 'bicolor', label: 'Dutch' },
        { id: 'patched', label: 'Broken' },
        { id: 'blaze', label: 'Blaze' },
    ],
}

/**
 * Fall back to the species default when the saved marking isn't offered for
 * the current species (e.g. a tabby cat switched to a horse).
 */
export function resolveMascotPattern(
    species: MascotSpecies,
    pattern: MascotPattern,
): MascotPattern {
    const options = MASCOT_PATTERN_OPTIONS[species]
    return options.some(o => o.id === pattern) ? pattern : options[0].id
}

/**
 * Which ear shapes each species offers, and what they're called there — a
 * rabbit's "upright" and a dog's "upright" are the same artwork at different
 * scales, but nobody calls a lop-eared rabbit "droopy".
 *
 * Order matters: the first entry is the species default.
 */
export const MASCOT_EAR_OPTIONS: Record<
    MascotSpecies,
    { id: MascotEarStyle; label: string }[]
> = {
    regular_dog: [
        { id: 'floppy', label: 'Drop' },
        { id: 'droopy', label: 'Hound' },
        { id: 'button', label: 'Button' },
        { id: 'folded', label: 'Rose' },
        { id: 'pointed', label: 'Pointed' },
        { id: 'upright', label: 'Upright' },
        { id: 'one_up', label: 'One up' },
    ],
    cat: [
        { id: 'pointed', label: 'Pointed' },
        { id: 'upright', label: 'Rounded' },
        { id: 'folded', label: 'Folded' },
        { id: 'one_up', label: 'One up' },
    ],
    rabbit: [
        { id: 'upright', label: 'Upright' },
        { id: 'droopy', label: 'Lop' },
        { id: 'one_up', label: 'One up' },
    ],
    horse: [{ id: 'pointed', label: 'Upright' }],
}

/**
 * Fall back to the species default when the saved style isn't offered for the
 * current species (e.g. a lop rabbit switched to a horse).
 */
export function resolveMascotEarStyle(
    species: MascotSpecies,
    earStyle: MascotEarStyle,
): MascotEarStyle {
    const options = MASCOT_EAR_OPTIONS[species]
    return options.some(o => o.id === earStyle) ? earStyle : options[0].id
}

export const DEFAULT_CLINIC_MASCOT: ClinicMascotIdentity = {
    species: 'regular_dog',
    primaryColor: '#FFFBF5',
    secondaryColor: '#F5E6D3',
    eyeColor: '#2F2A26',
    pattern: 'solid',
    furType: 'short',
    snout: 'long',
    earStyle: 'pointed',
    name: 'Rocket',
    nickname: 'Rocket',
}

/**
 * Preset iris swatches. Roughly ordered from the commonplace to the striking,
 * since most clinics will want a plausible eye before a novelty one.
 */
export const MASCOT_EYE_SWATCHES = [
    '#6B4423', // brown
    '#8B6F47', // hazel
    '#2F2A26', // near-black
    '#B8860B', // amber
    '#4E7A51', // green
    '#4A7FA5', // blue
    '#A9C7D8', // ice
    '#6B7280', // gray
] as const

/** Preset coat swatches for the editor (not an exhaustive palette). */
export const MASCOT_COAT_SWATCHES = [
    '#FFFBF5', // cream white (default primary)
    '#C4A484', // tan
    '#8B5A2B', // chocolate
    '#F5E6D3', // cream
    '#2F2A26', // near-black
    '#E8E4DF', // light gray
    '#6B7280', // gray
    '#F4A460', // sandy
    '#FFFFFF', // white
    '#D97706', // amber (cat-ish)
    '#78716C', // stone
] as const

/**
 * Compact entry-point label. Empty nickname → generic Ask Chckvet / Ask.
 */
export function askChckvetMascotLabel(
    mascot: ClinicMascotIdentity,
    opts?: { short?: boolean },
): string {
    const nick = mascot.nickname.trim()
    if (nick) return `Ask ${nick}`
    return opts?.short ? 'Ask' : 'Ask Chckvet'
}

export function sanitizeMascotNickname(raw: string): string {
    return raw.replace(/\s+/g, ' ').trim().slice(0, MASCOT_NICKNAME_MAX_CHARS)
}

export function sanitizeMascotName(raw: string): string {
    return raw.replace(/\s+/g, ' ').trim().slice(0, MASCOT_NAME_MAX_CHARS)
}
